// src/components/Public/PublicInvoiceVerifier.js (FINAL - With Search/Result Toggle)

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../apiService';
import * as Sentry from "@sentry/react";

// --- Helper Icon Components ---
const UserIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
const CompanyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m-1 4h1m6-12h1m-1 4h1m-1 4h1m-1 4h1" /></svg>;
const DateIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
const IdIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>;

function PublicInvoiceVerifier() {
    const [invoiceId, setInvoiceId] = useState('');
    const [foundInvoice, setFoundInvoice] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    
    // --- NEW STATE TO CONTROL VIEW ---
    const [isResultView, setIsResultView] = useState(false);

    const handleVerify = useCallback(async (e, idToVerify) => {
        if (e) e.preventDefault();
        setMessage('');
        setFoundInvoice(null);
        setIsLoading(true);

        const effectiveId = idToVerify || invoiceId;
        if (!effectiveId || !effectiveId.trim()) {
            setMessage("Please enter an Invoice ID to verify.");
            setIsLoading(false);
            setIsResultView(true);
            return;
        }

        try {
            const data = await api.getById('invoices', effectiveId.trim());
            if (data) {
                setFoundInvoice(data);
            } else {
                setMessage('The invoice ID you entered does not match our records. Please double-check the ID and try again.');
            }
        } catch (error) {
            console.error("Error verifying invoice:", error);
            if(window.Sentry) Sentry.captureException(error);
            setMessage('An error occurred during verification. Please try again later.');
        } finally {
            setIsLoading(false);
            setIsResultView(true);
        }
    }, [invoiceId]);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const idFromUrl = queryParams.get('invoiceNo'); 
        if (idFromUrl) {
            setInvoiceId(idFromUrl);
            handleVerify(null, idFromUrl); 
        }
    }, [handleVerify]);
    
    // --- NEW FUNCTION TO GO BACK TO SEARCH ---
    const handleBackToSearch = () => {
        setIsResultView(false);
        setFoundInvoice(null);
        setMessage('');
        setInvoiceId(''); // Clear input for fresh search
    };

    const getStatusStyles = (status) => {
        switch (status) {
            case 'Paid': return 'bg-green-100 text-green-800';
            case 'Unpaid': return 'bg-red-100 text-red-800';
            case 'Partially Paid': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const paymentPercentage = foundInvoice ? ((parseFloat(foundInvoice.totalPaid) || 0) / parseFloat(foundInvoice.totalAmount)) * 100 : 0;

    return (
        <div className="min-h-screen flex flex-col lg:flex-row bg-gray-100 font-sans">
            {/* --- LEFT SIDE: BRANDING --- */}
            <div className="relative lg:w-1/2 flex flex-col items-center justify-center p-8 py-12 bg-gradient-to-br from-red-700 to-red-900 text-white text-center">
                 <h1 className="text-5xl md:text-6xl font-extrabold mb-4">Welcome to Mix FM</h1>
                 <h2 className="text-2xl md:text-3xl font-light mb-12">Official Invoice Verification</h2>
                 <div className="flex justify-center items-center gap-x-2 md:gap-x-8">
                    <div className="bg-white rounded-lg p-1 md:p-2 shadow-lg"><img src="/Mix FM Logo 99.png" alt="Mix FM 99" className="h-12 md:h-20 w-auto" /></div>
                    <div className="bg-white rounded-lg p-1 md:p-2 shadow-lg"><img src="/Mix FM Logo 100.png" alt="Mix FM 100" className="h-12 md:h-20 w-auto" /></div>
                    <div className="bg-white rounded-lg p-1 md:p-2 shadow-lg"><img src="/Mix FM Logo 96.png" alt="Mix FM 96" className="h-12 md:h-20 w-auto" /></div>
                 </div>
                 <footer className="absolute bottom-4 text-center text-red-200 text-sm">
                    © {new Date().getFullYear()} Mix FM Network. All Rights Reserved.
                 </footer>
            </div>

            {/* --- RIGHT SIDE: VERIFICATION FORM & RESULT --- */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-white">
                <div className="w-full max-w-sm sm:max-w-md mx-auto">
                    
                    {/* --- VIEW TOGGLE LOGIC --- */}
                    {!isResultView ? (
                        // --- SEARCH VIEW ---
                        <div>
                            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2 text-center">Verify Your <span className="text-red-700">Invoice</span></h3>
                            <p className="text-center text-gray-500 mb-8">Enter your Invoice ID below to confirm its authenticity.</p>
                            <form onSubmit={handleVerify} className="space-y-6">
                                <div>
                                    <label htmlFor="invoiceId" className="block text-base font-medium text-gray-700 mb-2">Invoice ID</label>
                                    <input type="text" id="invoiceId" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} required className="block w-full px-5 py-3 border border-gray-300 rounded-lg font-mono tracking-wider"/>
                                </div>
                                <button type="submit" className="w-full py-4 px-6 rounded-xl shadow-lg text-xl font-bold text-white bg-red-600 hover:bg-red-700 transition-transform transform hover:scale-105" disabled={isLoading}>
                                    {isLoading ? 'Verifying...' : 'Verify Invoice'}
                                </button>
                            </form>
                        </div>
                    ) : (
                        // --- RESULT VIEW ---
                        <div className="animate-fade-in">
                            <button onClick={handleBackToSearch} className="flex items-center gap-2 text-sm font-semibold text-rose-600 hover:text-rose-800 mb-6">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                Back to Search
                            </button>

                            {foundInvoice && (
                                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 md:p-8">
                                    <div className="text-center mb-6"><svg className="w-16 h-16 text-green-500 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg><h2 className="text-3xl font-bold text-gray-800 mt-2">VERIFIED</h2><p className="text-gray-500">This is a legitimate invoice from our records.</p></div>
                                    <div className="border-t border-b border-gray-200 py-6 space-y-4"><h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Invoice Summary</h3><div className="flex items-center gap-4"><UserIcon /><div><div className="text-sm text-gray-500">Client Name</div><div className="font-semibold text-gray-800">{foundInvoice.clientName}</div></div></div><div className="flex items-center gap-4"><CompanyIcon /><div><div className="text-sm text-gray-500">Company</div><div className="font-semibold text-gray-800">{foundInvoice.clientCompany}</div></div></div><div className="flex items-center gap-4"><DateIcon /><div><div className="text-sm text-gray-500">Invoice Date</div><div className="font-semibold text-gray-800">{new Date(foundInvoice.invoiceDate).toLocaleDateString()}</div></div></div><div className="flex items-center gap-4"><IdIcon /><div><div className="text-sm text-gray-500">Invoice ID</div><div className="font-semibold text-gray-800 font-mono">{foundInvoice.id?.toString().substring(0,8)}</div></div></div></div>
                                    <div className="pt-6"><h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Financial Status</h3><div className="space-y-3"><div className="flex justify-between items-center text-lg"><span className="text-gray-600">Total Amount:</span><span className="font-bold text-gray-900">PKR {(parseFloat(foundInvoice.totalAmount) || 0).toLocaleString('en-PK')}</span></div><div className="flex justify-between items-center text-lg"><span className="text-gray-600">Status:</span><span className={`px-3 py-1 text-sm font-bold rounded-full ${getStatusStyles(foundInvoice.status)}`}>{foundInvoice.status}</span></div></div>
                                    {foundInvoice.status !== 'Unpaid' && (<div className="mt-4"><div className="w-full bg-gray-200 rounded-full h-2.5"><div className="bg-green-500 h-2.5 rounded-full" style={{width: `${paymentPercentage}%`}}></div></div><div className="flex justify-between text-sm mt-1"><span className="font-semibold text-green-700">Paid: PKR {(parseFloat(foundInvoice.totalPaid) || 0).toLocaleString('en-PK')}</span><span className="font-semibold text-red-700">Pending: PKR {(parseFloat(foundInvoice.pendingAmount) || 0).toLocaleString('en-PK')}</span></div></div>)}</div>
                                </div>
                            )}
                            {message && (
                                <div className="bg-red-50 border-l-4 border-red-500 text-red-800 p-4 rounded-r-lg">
                                    <h4 className="font-bold">Verification Failed</h4>
                                    <p>{message}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PublicInvoiceVerifier;
